import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import './LoginPage.css';
import { BrandTile, Wordmark } from '../components/BrandMark';
import { useBrand, useInstanceName } from '../InstanceName';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const instanceName = useInstanceName();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fromPath = (location.state as LocationState | null)?.from ?? '/';

  useEffect(() => {
    if (status === 'authenticated') {
      navigate(fromPath, { replace: true });
    }
  }, [status, navigate, fromPath]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || !username || !password) return;

    setSubmitting(true);
    setError(null);
    try {
      await login(username, password);
    } catch (err) {
      setError(resolveError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__void" aria-hidden="true">
        <div className="login-page__void-blob login-page__void-blob--one" />
        <div className="login-page__void-blob login-page__void-blob--two" />
      </div>
      <form className="login-card glass" onSubmit={onSubmit}>
        <div className="login-card__brand">
          <BrandTile className="login-card__logo" tile={40} glyph={32} />
          <div className="login-card__brand-text">
            <Wordmark className="login-card__brand-name" name={instanceName} />
            <div className="login-card__brand-tag">{brand.tagline}</div>
          </div>
        </div>

        <h1 className="login-card__title">Sign in</h1>

        <label className="login-field">
          <span className="login-field__label">Username</span>
          <input
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={submitting}
            required
            autoFocus
          />
        </label>

        <label className="login-field">
          <span className="login-field__label">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            required
          />
        </label>

        {error && <div className="login-card__error">{error}</div>}

        <button
          type="submit"
          className="login-card__submit"
          disabled={submitting || !username || !password}
        >
          {submitting ? 'Signing in...' : 'Sign in'}
        </button>

        <div className="login-card__footer">
          Sessions use HttpOnly cookies. Protected by JWT + rate limiting.
        </div>
      </form>
    </div>
  );
}

function resolveError(err: unknown): string {
  if (!(err instanceof Error)) return 'Sign-in failed. Try again.';
  const msg = err.message;
  if (msg.includes('429')) return 'Too many attempts. Please wait a moment.';
  if (msg.includes('401')) return 'Invalid username or password.';
  if (msg.includes('400')) return 'Username and password required.';
  return 'Sign-in failed. Please try again.';
}
